import { GuideApiService } from './GuideApiService';
import { IntegrationService } from './IntegrationService'

export class ActionService {
  static answers = [];
  /**
   * Get an action on the server by the actions name.
   * /api/actions/name/:actionName
   * @param {string} actionName - The name of the action to trigger.

   * @returns {Promise} - The promise that resolves to the response of the action.
   */
  static getActionByName(actionName) {
    return GuideApiService.makeRequest('GET', `/actions/name/${actionName}`);
  }

  /**
   * Get an action on the server by searching for any action with a matching trigger.position.
   * This is used to get actions by their button positions
   * /api/actions/buttons/:position
   * @param {string} position - The button position to search for. Defined in ThemeManifest
   * @returns {Promise} - The promise that resolves to the response of the action.
   */
  static getActionByPosition(page, position) {
    return GuideApiService.makeRequest('GET', `/actions/buttons/${page}/${position}`);
  }

  /**
   * Get an action on the server by searching for any action with a matching trigger.position.
   * This function supports flexible parameter inputs to accommodate different usage scenarios.
   * /api/actions/triggerType/:triggerType/:pageName?/:targetQuestionId?
   * @param {string} triggerType - The button position to search for. onLoad, onLeave, button, productRecommendation
   * @param {string} [pageName] - Optional - the page name related to the trigger
   * @param {string} [targetQuestionId] - Optional - the targetQuestionId related to the trigger
   * @returns {Promise} - The promise that resolves to the response of the action.
   */
  static getActionsByTriggerType(triggerType, pageName = '', targetQuestionId = '') {
    let url = `/actions/triggerType/${triggerType}`;
    
    if (pageName) {
      url += `/${pageName}`;
      if (targetQuestionId) {
        url += `/${targetQuestionId}`;
      }
    }

    return GuideApiService.makeRequest('GET', url);
  }


    /**
   * Extract the first trigger found within an action that matches the given position
   * This is used by the ActionButton to get a list of triggers
   * Returns null if none is found.
   * 
   * @param {ActionModel} action - The action to extract from.
   * @param {string} position - The button position to search for. Defined in ThemeManifest
   * @returns {ActionTriggerModel} - The trigger found within the action matching the position
   */
  static extractButtonTriggerByPosition(action, position) {
    for (let trigger of action.triggers) {
      if(trigger.type == 'button' && trigger.position == position){
        return trigger;
      }
    }
    return null;
  }

  /**
   * Extract the first trigger found within an action that matches the given position
   * This is used by the ActionButton to get a list of triggers
   * Returns null if none is found.
   * 
   * @param {ActionModel} action - The action to extract from.
   * @param {string} triggerType - The triggerType of the trigger to find
   * @param {string} [pageName] - The targetPage of the trigger to find
   * @param {string} [targetQuestionId] - The targetQuestionId of the trigger to find
   * @returns {ActionTriggerModel} - The trigger found within the action matching the position
   */
  static extractTriggerByTriggerType(action, triggerType, pageName, targetQuestionId) {
    for (let trigger of action.triggers) {
      if (trigger.type == triggerType && 
         (!pageName || trigger.targetPage == pageName) && 
         (!targetQuestionId || trigger.targetQuestionId == targetQuestionId)) {
        return trigger;
      }
    }
    return null;
  }

  /**
   * Extracts all triggers found within an action that matches the given position
   * This is used by the ActionButton to get a list of triggers
   * Returns null if none is found.
   * 
   * @param {ActionModel} action - The action to extract from.
   * @param {string} triggerType - The triggerType of the trigger to find
   * @param {string} [pageName] - The targetPage of the trigger to find
   * @param {string} [targetQuestionId] - The targetQuestionId of the trigger to find
   * @returns {ActionTriggerModel} - The trigger found within the action matching the position
   */
  static extractAllTriggersByTriggerType(action, triggerType, pageName, targetQuestionId) {
    let output = [];
    for (let trigger of action.triggers) {
      if (trigger.type == triggerType && 
         (!pageName || trigger.targetPage == pageName) && 
         (!targetQuestionId || trigger.targetQuestionId == targetQuestionId)) {
        output.push(trigger);
      }
    }
    return output;
  }

  /**
   * Extract all IntegrationInputModel params for a given action.
   * This is used by the ActionButton to get a list of params when running the command
   * 
   * Integrations and Actions can have "runtime" inputs applied to them
   * These lists of inputs will be combined into a unique key set based on the input name
   * We then extract values for each key from answers and session storage
   * 
   * @param {ActionModel} action - The action to extract from.
   * @param {string} position - The button position to search for. Defined in ThemeManifest
   * @returns {Object} - An object containing keys matching the list of IntegrationInputs, and values found from the guide
   */
  static async extractActionParams(action, neuroStateUser, guideActionServiceMapper) {

    //mock
    // TODO: get answers from guide
    
    const answers = this.answers;
    console.log('extractActionParams answers length ' + answers.length);
    GuideApiService.useAnswers(this.answers);

    if (action?.targetType == 'integration'){
      const output = {};

      //start our param list with the inputs from the integration
      const integration = await IntegrationService.getIntegrationByName(action.targetId);
      let paramKeys = [];
      if(integration.inputs && integration.inputs[action.command])
        paramKeys = integration.inputs[action.command].map(e => {
          output[e?.name] = GuideApiService.transformValueWithPlaceholders(e?.value || e?.defaultValue);
          return e.name
        });
      

      // add any missing inputs applied to the action
      for (let input of action.inputs) {
        if (!paramKeys.includes(input.name)) { 
          paramKeys.push(input.name);
        }
        output[input?.name] = GuideApiService.transformValueWithPlaceholders(input?.value || input?.defaultValue);
      }

      // get values from guide.answers
      // TODO: add an option to ActionInputs to allow/prevent
      // auto fill answers/session. Right now everything auto fills
      // and this could cause a collision between matching named
      // fields on the command.inputs and the guides answers or url params
      for (let answer of answers) {
        if (paramKeys.includes(answer.appkey)) {
          output[answer.appkey] = GuideApiService.transformValueWithPlaceholders(answer.value);
        }
      }

      // get values from session
      for (let key in window?.session?.ignite?.actionsMetadata) {
        if (paramKeys.includes(key)) {
          output[key] = GuideApiService.transformValueWithPlaceholders(window.session.ignite.actionsMetadata[key].value);
        }
      }

      // get values from NEURO session
      try {
        const sessionStorageKey = `${neuroStateUser?.uuid}-localAnswers`;
        const storedValues = window.sessionStorage.getItem(sessionStorageKey);
        const values = JSON.parse(storedValues || '[]');
      
        if (typeof values === 'object' && values !== null) {
          Object.keys(values).forEach(key => {
            let value = values[key]?.value;
            if (Array.isArray(value)) {
              value = value[0];
            }
            const finalKey = this.mapGuideSpecificValues(key, guideActionServiceMapper) || key;
            output[finalKey] = value;
          });
        } else {
          console.error('Parsed value is not an object:', values);
        }
      } catch (error) {
        console.error('Error processing session storage values:', error);
      }

      return output;
    } 
    // TODO: add support for extracting Job params if viable later
    
    return {};
  }

/**
 * Map guide specific values to action system specific values if present in mapper provided.
 * @param {String} key - value to map
 * @param {Object} mapper - key: guide specific key, value: action specific key needed for processing. ex: {sendPlan: 'to'}
 * @returns {String} - String value mapped to action system specific value or original value if not found in mapper.
 */
  static mapGuideSpecificValues(key, mapper) {
    if (typeof key !== 'string' || typeof mapper !== 'object' || mapper === null) {
      return null;
    }
  
    return mapper.hasOwnProperty(key) ? mapper[key] : key;
  }

  static saveActionMetadata(key, data) {
    if (typeof data == 'object')
      window.sessionStorage[`ignite.actionsMetadata.${key}`] = JSON.stringify(data);
    else
      window.sessionStorage[`ignite.actionsMetadata.${key}`] = data;
  }

  //field: 'inputs' || 'outputs'
  static saveFields(field, response){
    const savefield = `${field.replace(/s$/, '')}Saves`;
    for(let outputKey in response['$$ignite'][field]) {
      if(response['$$ignite'][savefield][outputKey] == 'answers') {
        window.ignite.answerService.saveAnswer({
          appkey: outputKey,
          value: response['$$ignite'][field][outputKey] || response[outputKey],
          timestamp: new Date()
        }).then();
      } else if (response['$$ignite'][savefield][outputKey] == 'session'){
        this.saveActionMetadata(outputKey, response['$$ignite'][field][outputKey]);
      }
    }
  }
 
  /**
   * Trigger an action on the server.
   * /api/actions/:actionName/trigger/:triggerId
   * @param {string} actionName - The name of the action to trigger.
   * @param {string} triggerId - the id of the trigger to run. 
   *                             If no trigger Id is provided the first trigger will be used
   * @param {Object} params - The parameters to pass with the action.
   * @returns {Promise} - The promise that resolves to the response of the action.
   */
  static triggerAction(action, triggerId, params) {
    return GuideApiService.makeRequest('POST', `/actions/${action.name}/trigger/${triggerId}`, params).then((response) => {
      if (response['$$ignite'] && response['$$ignite'].outputs && response['$$ignite'].outputSaves) {
        this.saveFields('outputs', response);
      }
      if (response['$$ignite'] && response['$$ignite'].inputs && response['$$ignite'].inputSaves) {
        this.saveFields('inputs', response);
      }
      return response;
    });
  }

  // Pass in a pageObj grabbed from usePages().getCurrentPage()
  // Pass in currentPageUrl from usePages()
  static getPageName(pageObj, pageUrl){
    if(!pageObj) {
      if(pageUrl?.includes('/summary'))
        return 'SummaryPage';
      if(pageUrl?.includes('/details'))
        return 'DetailsPage';
    }
    if(pageObj?.questionGroups || pageObj?.path) return 'SurveyPage';
    return 'LandingPage';
  }

  /**
   * Ticks the action system
   * A tick should be performed anytime the guide is changing pages, or is entering a new lifecycle state.
   * This function will fire off all actions triggered by lifecycle changes automatically
   * There is typically no UI feedback on these types of actions but this function should eventually support that
   * 
   * e.g. state = onLeave
   *      page = SurveyPage
   *      targetQuestionId = abc123
   * 
   * e.g. state = onLoad
   *      page = SummaryPage
   * 
   * 
   * @param {string} state - The string name of the state
   * @param {string} page - The string name of the page component
   * @param {string} targetQuestionId - The Id of the question
   * 
   * @returns {Promise} - The promise that resolves to the response of the action.
   */
  static async tick(state, pageObj, pageUrl, answers){
    try{
      this.answers = answers;
      const pageName = this.getPageName(pageObj, pageUrl);
      const pageKey = pageObj?.page?.key

      //This does not work when running diana locally cause mock diana doesnt have a dialogueApiConfig
      const actions = await this.getActionsByTriggerType(state, pageName, pageKey);
      const promises = [];
      for (let action of actions) {
        const trigger = this.extractTriggerByTriggerType(action, state, pageName, pageKey);
        if(trigger)
          promises.push(this.triggerAction(action, trigger?._id, await this.extractActionParams(action)));
      }
      Promise.all(promises).then(() => {
        console.log(`${promises.length} actions triggered`);
      });
    }catch(e){
      console.error('ActionService tick error', e);
    }
  }
}

// Export the class
export default ActionService;
